<template>
  <va-card :title="$t('menu.orders')">
    <div class="row">
      <div class="flex xs12 sm2">
        <va-date-picker
          :label="$t('tables.date')"
          v-model="delivery_date"
        />
      </div>
      <div class="flex xs12 md2">
        <va-input
          v-model="searchName"
          :label="$t('tables.orders.code')"
          removable
        >
        </va-input>
      </div>
      <div class="flex md1">
        <va-button
          small
          color="secondary"
          icon="fa fa-search"
          class="ma-2"
          @click="readItems(page, searchName)"
        >
          {{ $t('menu.search') }}
        </va-button>
      </div>
      <div class="flex xs12 md3 offset--md4">
        <va-select
          v-model="perPage"
          :value="perPage"
          :label="$t('tables.perPage')"
          :options="perPageOptions"
        />
      </div>
    </div>

    <div v-show="loading" class="text--center pb-4">
      <div class="flex-center spinner-box">
        <slot name="loading">
          <spring-spinner
            slot="loading"
            :animation-duration="currentDuration"
            :is="spload.name"
            :color="spinnersColor"
            :size="config.size"
          />
        </slot>
      </div>
    </div>

    <va-data-table
      :fields="fields"
      :data="orders"
      :per-page="parseInt(perPage)"
      :total-pages="parseInt(totalPages)"
      api-mode
      v-show="!loading"
      no-pagination
      @page-selected="readItems"
    >

    <template slot="status" slot-scope="props">
      <va-badge v-if="props.rowData.returned == true" color="orangeRed" class="ma-0">
          {{ $t('tables.orders.returned') }}
      </va-badge>

      <va-badge v-if="props.rowData.quarantine" color="danger" class="ma-0">
        {{ $t('menu.quarantine') }}
      
      <va-badge v-if="props.rowData.refused" color="orange" class="ma-0">
        {{ $t('tables.orders.refused') }}
      </va-badge>
      </va-badge>
    </template>

      <template slot="actions" slot-scope="props">
        <va-button v-if="props.rowData.hasReport" small color="danger" class="ma-0">
          {{ $t('tables.report') }}
        </va-button>

        <va-button v-if="props.rowData.order_errors.length > 0"
          small
          inline
          color="danger"
          icon="fa fa-list-alt"
          class="ma-1"
          :to="{ name: 'order-errors', params: { orderId: props.rowData.id, assemblyLine: 4, order: props.rowData.code, delivery_date: delivery_date, current_page: current_page, searchName: searchName, perPage: perPage } }"
        >
          {{ $t('menu.order_error') }}
        </va-button>

        <va-button v-if="ability.can('update', 'Mounting')"
            small
            inline
            color="green"
            icon="fa fa-edit"
            class="mr-1"
            :to="{ name: 'form-mounting-order', params: { pallet_type: props.rowData.pallet_type, pallet_type_id: props.rowData.pallet_type_id, order_step: props.rowData.order_step, order_step_name: props.rowData.order_step_name, page_name: 'layer_picking',  order_id: props.rowData.id, token: (props.rowData.token != null) ? props.rowData.token : '', delivery_date: delivery_date, current_page: current_page, searchName: searchName, perPage: perPage } }" 
          >
            {{ $t('menu.edit') }}
        </va-button>

        <va-button v-if="props.rowData.order_histories_count > 0"
          small
          inline
          color="green"
          icon="fa fa-list-alt"
          class="ma-1"
          :to="{ name: 'order-history', params: { orderId: props.rowData.id, assemblyLine: 4, order: props.rowData.code, delivery_date: delivery_date, current_page: current_page, searchName: searchName, perPage: perPage } }"
        >
          {{ $t('menu.order_history') }}
        </va-button>
        
        <va-button
          small
          inline
          color="dark"
          icon="fa fa-list-alt"
          class="ma-0"
          :to="{ name: 'list-order-products', params: { orderId: props.rowData.id, assemblyLine: 4, order: props.rowData.code, delivery_date: delivery_date, current_page: current_page, searchName: searchName, perPage: perPage } }" 
        >
          {{ $t('menu.products') }}
        </va-button>

        <va-button
          small
          inline
          color="greenLight"
          icon="fa fa-list-alt"
          class="ma-1"
          :to="{ name: 'list-sorted-order-products', params: { orderId: props.rowData.id, assemblyLine: 4, order: props.rowData.code, delivery_date: delivery_date, current_page: current_page, searchName: searchName, perPage: perPage } }" 
        >
          {{ $t('menu.order_products') }}
        </va-button>
      </template>
    </va-data-table>

      <Pagination 
        :currentPage="current_page"
        :totalPages="totalPages"
        :onChange="onPageChange"
        v-show="!loading"
      />

  </va-card>
</template>

<script>

import { debounce } from 'lodash'
import OrdersAPI from '@/services/OrdersAPI'
import RoleAPI from '@/services/RoleAPI'
import * as spinners from 'epic-spinners'
import { showError } from '@/global'

import { mapState } from "vuex";
import { Ability } from '@casl/ability'
export const ability = new Ability()

import Pagination from '../../shared/Pagination'

export default {
  components: {
    ...spinners,
    Pagination
  },
  data () {
    return {
      ability: [],
      term: null,
      role: '',
      roleOptions: [],
      loading: false,
      config: {
        size: 80,
        group: 4,
        duration: 1500,
      },
      spinnersColor: this.$themes.secondary,
      currentDuration: 1500,
      searchName: "",
      perPage: "10",
      totalPages: 0,
      perPageOptions: ['10', '20', '30', '50'],
      orders: [],
      delivery_date: '',
      current_page: '',
      last_refresh: new Date()
    }
  },

  channels: {
    NeedRefreshOrders: {
      received(data) {
        const last_received_refresh = ( new Date() - this.last_refresh) / 1000
        
        if (last_received_refresh >= 10) {
          this.last_refresh = new Date()
          this.readItems()
        }
      }
    }
  },

  beforeMount(){
    this.ability = ability.update(this.rules)
  },
  
  mounted () {
    this.$cable.subscribe({ channel: 'NeedRefreshOrders', company: this.currentUser.company })
          
    this.perPage = (this.$route.params.perPage !== undefined) ? `${this.$route.params.perPage}` : "10"
    this.searchName = (this.$route.params.searchName !== undefined) ? `${this.$route.params.searchName}` : ""
    this.delivery_date = (this.$route.params.delivery_date !== undefined) ? `${this.$route.params.delivery_date}` : ""

    this.readItems(this.$route.params.current_page, this.searchName)
  },
  computed: {
    ...mapState(['rules', 'currentLocality', 'currentUser']),

    fields () {
      return [
      {
        name: 'id',
        title: this.$t('tables.orders.id'),
        width: '5%',
      },
      {
        name: '__slot:status',
        title: this.$t('tables.orders.status'),
        width: '5%',
        dataClass: 'text-center',
      },
      {
        name: 'locality_name',
        title: this.$t('tables.orders.locality_name'),
        width: '10%',
      },
      {
        name: 'token',
        title: this.$t('tables.orders.token'),
        width: '10%',
      },
      {
        name: 'user',
        title: this.$t('tables.orders.mounter'),
        width: '10%',
      },
      {
        name: 'code',
        title: this.$t('tables.orders.code'),
        width: '10%',
      }, 
      {
        name: 'vehicle',
        title: this.$t('tables.orders.vehicle'),
        width: '10%',
      },
      {
        name: 'priority',
        title: this.$t('tables.orders.priority_name'),
        width: '10%',
      },
      {
        name: 'quantity_boxes',
        title: this.$t('tables.loads.quantity_boxes'),
      },
      {
        name: 'weight',
        title: this.$t('tables.loads.weight'),
      },
      {
        name: 'quantity_boxes',
        title: this.$t('tables.loads.quantity_boxes'),
      },
      {
        name: 'load',
        title: this.$t('tables.orders.load_code'),
        width: '5%',
      },
      {
        name: 'delivery_date',
        title: this.$t('tables.orders.delivery_date'),
        width: '5%',
      },
      {
        name: 'pallet_type',
        title: this.$t('tables.orders.pallet_kind'),
        width: '10%',
      },
      {
        name: 'note',
        title: this.$t('tables.orders.note'),
        width: '10%',
      },
      {
        name: '__slot:actions',
        dataClass: 'text-right',
      }]
    },
    spload () {
      return spinners.ScalingSquaresSpinner
    }
  },
  watch: {
    perPage: function (newVal) {
      this.perPage = newVal
      this.readItems(this.current_page)
    },
    searchName: function (newVal) {
      if (!newVal) {
        this.readItems()
      }
    },
    delivery_date: function (newVal) {
      this.readItems()
    },

    '$store.state.currentLocality': function() {
      this.readItems()
    }
  },
  methods: {
    onPageChange(page) {
      this.readItems(page)
    },
    readItems(page, searchName) {
      this.loading = true

      const params = {
        page: page,
        quantity: this.perPage,
        name: this.searchName,
        status: 3, // mounting
        role: 4,  // operator_lp
        locality: this.currentLocality.id,
        delivery_date: this.delivery_date
      }
      this.current_page = this.$route.params.current_page

      OrdersAPI.getOrdersByStatus(params).then(response => {
        this.totalPages = response.data.total_pages
        this.current_page = response.data.current_page
        this.orders = response.data.orders
        this.loading = false
      }).catch(e => {
        showError(e)
        this.loading = false
      })
    },
  },
}
</script>
